import type { SourceInfo } from "#/types";

import { useAuth } from "#/feature/auth/auth-context";
import { useRequireAuth } from "#/feature/auth/use-require-auth";
import {
  Download,
  ExternalLink,
  MoreVertical,
  Plus,
  Power,
  PowerOff,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cx } from "styled-system/css";

import {
  pageStyles,
  headerStyles,
  titleStyles,
  sourceListStyles,
  actionBtnStyles,
  loadingStyles,
  emptyStyles,
  sourceItemStyles,
  sourceTitleStyles,
  sourceLinkStyles,
  dropdownContainerStyles,
  dropdownMenuStyles,
  dropdownItemStyles,
  modalOverlayStyles,
  modalBoxStyles,
  modalHeaderStyles,
  modalTitleStyles,
  modalBodyStyles,
  importTextareaStyles,
  modalActionsStyles,
  modalBtnStyles,
} from "./sources.css";
import { useSources } from "./use-sources";
import { Button } from "#/components/button/button";
import { Input } from "#/components/input/input";
import { Card } from "#/components/card/card";

export function Sources() {
  const allowed = useRequireAuth();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const {
    sources,
    loadingState,
    showImport,
    importJson,
    setShowImport,
    setImportJson,
    exportSource,
    handleImport,
    toggleDisabled,
    handleDelete,
  } = useSources(isAdmin);

  const renderContent = () => {
    if (loadingState) {
      return <div className={loadingStyles}>Loading...</div>;
    }

    if (sources.length === 0) {
      return <div className={emptyStyles}>No sources configured yet.</div>;
    }

    return (
      <div className={sourceListStyles} data-debug-id="sources-list">
        {sources.map((sourceItem: SourceInfo) => (
          <SourceItem
            key={sourceItem.id}
            source={sourceItem}
            isAdmin={isAdmin}
            onExport={() => void exportSource(sourceItem.id)}
            onToggleDisabled={() => void toggleDisabled(sourceItem.id, sourceItem.disabled)}
            onDelete={() => void handleDelete(sourceItem.id)}
          />
        ))}
      </div>
    );
  };

  if (!allowed) return null;

  return (
    <div className={pageStyles}>
      <div className={headerStyles}>
        <h1 className={titleStyles}>Sources</h1>
        {isAdmin && (
          <Button onClick={() => setShowImport(true)} className={actionBtnStyles}>
            <Plus size={16} style={{ marginRight: "0.5rem" }} />
            Import
          </Button>
        )}
      </div>

      {renderContent()}

      {showImport && (
        <ImportModal
          importJson={importJson}
          setImportJson={setImportJson}
          onClose={() => setShowImport(false)}
          onImport={handleImport}
        />
      )}
    </div>
  );
}

interface SourceItemProps {
  source: SourceInfo;
  isAdmin: boolean;
  onExport: () => void;
  onToggleDisabled: () => void;
  onDelete: () => void;
}

const SourceItem = ({ source, isAdmin, onExport, onToggleDisabled, onDelete }: SourceItemProps) => {
  const { id, config, disabled } = source;
  const baseUrl = config.meta.base_url;
  const needsExtension = config.meta.needs_extension;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={sourceItemStyles({ disabled })}>
      <div className={sourceTitleStyles}>
        {id}
        {needsExtension && (
          <span style={{ fontSize: "12px", color: "gray", fontWeight: "400" }}>(Extension)</span>
        )}
        {disabled && (
          <span style={{ fontSize: "12px", color: "var(--colors-error)", fontWeight: "400" }}>
            (Disabled)
          </span>
        )}
        <a
          href={baseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={sourceLinkStyles}
          title="Go to source"
        >
          <ExternalLink size={16} />
        </a>
      </div>

      <div className={dropdownContainerStyles} ref={menuRef}>
        <Button
          variant="ghost"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{ padding: "0.5rem", height: "auto", color: "inherit" }}
        >
          <MoreVertical size={20} />
        </Button>

        {isMenuOpen && (
          <div className={dropdownMenuStyles}>
            {isAdmin && (
              <div
                className={dropdownItemStyles()}
                onClick={() => {
                  onToggleDisabled();
                  setIsMenuOpen(false);
                }}
              >
                {disabled ? <Power size={16} /> : <PowerOff size={16} />}
                {disabled ? "Enable" : "Disable"}
              </div>
            )}

            <div
              className={dropdownItemStyles()}
              onClick={() => {
                onExport();
                setIsMenuOpen(false);
              }}
            >
              <Download size={16} />
              Export
            </div>

            {isAdmin && (
              <div
                className={dropdownItemStyles({ danger: true })}
                onClick={() => {
                  onDelete();
                  setIsMenuOpen(false);
                }}
              >
                <Trash2 size={16} />
                Delete
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface ImportModalProps {
  importJson: string;
  setImportJson: (value: string) => void;
  onClose: () => void;
  onImport: () => void;
}

const ImportModal = ({ importJson, setImportJson, onClose, onImport }: ImportModalProps) => (
  <div className={modalOverlayStyles}>
    <Card className={modalBoxStyles}>
      <Card.Header className={modalHeaderStyles}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <div
            style={{
              padding: "0.5rem",
              backgroundColor: "color-mix(in srgb, var(--colors-primary) 15%, transparent)",
              borderRadius: "0.5rem",
            }}
          >
            <Upload size={20} style={{ color: "var(--colors-primary)" }} />
          </div>
          <h2 className={modalTitleStyles}>Import Crawler</h2>
        </div>
        <Button variant="ghost" size="small" onClick={onClose} style={{ padding: "0.5rem" }}>
          <X size={20} />
        </Button>
      </Card.Header>
      <Card.Body className={modalBodyStyles}>
        <Input
          as="textarea"
          value={importJson}
          onChange={(event: any) => setImportJson(event.target.value)}
          placeholder="Paste crawler JSON here..."
          className={importTextareaStyles}
        />
        <div className={modalActionsStyles}>
          <Button
            variant="ghost"
            onClick={onClose}
            className={modalBtnStyles()}
            style={{ color: "inherit" }}
          >
            Cancel
          </Button>
          <Button
            onClick={onImport}
            disabled={!importJson}
            className={modalBtnStyles({ primary: true })}
          >
            Import Source
          </Button>
        </div>
      </Card.Body>
    </Card>
  </div>
);
